// What every Reports tab shares (F-ANL-03): the formatting of its figures, the "compared with the previous period"
// delta on a headline figure, the loading, failure and permission states, and the CSV export of a tab's table.
//
// The delta is `@clavian/domain/reports`' `reportDeltaOf` — empty when either period has no data (a comparison with
// nothing measured is not a rise) — and says what it compares against in words.

import type { Money } from '@clavian/api-client';
import { reportDeltaOf } from '@clavian/domain/reports';
import { useTranslation, type useVenueFormatter } from '@clavian/i18n/react';
import { Button, EmptyState, type StatDelta } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { Forbidden, TableSkeleton } from '../../ui';

export type Format = ReturnType<typeof useVenueFormatter>;

export const NO_DATA = '—';

/** A day's short label on an axis: "28 Sep". */
export function dayShort(format: Format, date: string): string {
  return format.custom(`${date}T12:00:00Z`, { day: 'numeric', month: 'short' });
}

/** A day's full label in a tooltip: "Mon, 28 Sep 2026". */
export function dayLong(format: Format, date: string): string {
  return format.custom(`${date}T12:00:00Z`, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** A range in words: "19 – 25 Sep 2026", or one day. */
export function rangeLabel(format: Format, from: string, to: string): string {
  if (from === to) return format.custom(`${from}T12:00:00Z`, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${format.custom(`${from}T12:00:00Z`, { day: 'numeric', month: 'short' })} – ${format.custom(`${to}T12:00:00Z`, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** Money in whole rupees (₹1,55,798), for the big figures, axes and tooltips; tables keep the paise. */
export function moneyShort(format: Format, amount: number, currency: string): string {
  return format.money({ amount, currency }, 'noFraction');
}

/** An amount on a chart's axis: ₹0, ₹5K, ₹1.5L. Never a figure to reconcile — those are in the tables. */
export function moneyTick(format: Format, amount: number, currency: string): string {
  return format.money({ amount: Math.round(amount), currency }, 'compact');
}

/** A StatCard's `sparkline` prop: the period's daily values, only when two or more days have one. */
export function sparkOf(values: readonly (number | null | undefined)[]): { sparkline?: number[] } {
  const known = values.filter((value): value is number => value !== null && value !== undefined);
  return known.length > 1 ? { sparkline: values.map((value) => value ?? 0) } : {};
}

/** The average of the days that have a value, or null. */
export function averageOf(values: readonly number[]): number | null {
  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** A money figure or "—". */
export function moneyOrDash(format: Format, value: Money | null | undefined): string {
  return value === null || value === undefined ? NO_DATA : format.money(value);
}

/** Basis points as a percentage or "—". */
export function percentOf(format: Format, basisPoints: number | null | undefined, digits = 0): string {
  return basisPoints === null || basisPoints === undefined ? NO_DATA : format.number(basisPoints / 10_000, { style: 'percent', maximumFractionDigits: digits });
}

/**
 * The StatCard delta of a figure against the previous period's, or nothing when either has no data. `upIsGood`
 * decides the colour (a rise in refunds is not good news); the caption names the period compared with.
 */
export function deltaOf(current: number | null | undefined, previous: number | null | undefined, caption: string, format: Format, upIsGood = true): StatDelta | undefined {
  const delta = reportDeltaOf(current, previous);
  if (delta === null) return undefined;
  const value = delta.basisPoints === null ? NO_DATA : format.number(Math.abs(delta.basisPoints) / 10_000, { style: 'percent', maximumFractionDigits: 0 });
  const tone = delta.trend === 'flat' ? 'neutral' : (delta.trend === 'up') === upIsGood ? 'ok' : 'danger';
  return { value, trend: delta.trend, tone, caption };
}

/** A tab's query, before it has data: a skeleton, the failure with a retry, or the permission refusal. */
export function TabPending({ query, unavailable, forbidden }: { readonly query: { readonly isError: boolean; readonly error: unknown; readonly refetch: () => unknown }; readonly unavailable: string; readonly forbidden: string }): ReactNode {
  if (query.isError && problemCode(query.error) === PROBLEM.forbidden) return <Forbidden message={forbidden} />;
  if (query.isError) return <LoadFailure error={query.error} message={unavailable} onRetry={() => void query.refetch()} />;
  return <TableSkeleton />;
}

/** A share of a whole as a whole percentage ("38%"); "<1%" for a sliver, so a slice never reads 0%. */
export function shareWords(format: Format, share: number): string {
  if (share > 0 && share < 0.005) return `<${format.number(0.01, { style: 'percent' })}`;
  return format.number(share, { style: 'percent', maximumFractionDigits: 0 });
}

/** "Nothing in this range". */
export function TabEmpty({ title, description }: { readonly title: string; readonly description: string }): ReactNode {
  return <EmptyState icon={<NavIcon name="reports" />} title={title} description={description} />;
}

/** A money amount as a CSV cell: major units with the currency's two decimals, written from the integer. */
export function csvMoney(value: Money | null | undefined): string {
  if (value === null || value === undefined) return '';
  const negative = value.amount < 0;
  const minor = Math.abs(value.amount);
  return `${negative ? '-' : ''}${Math.trunc(minor / 100)}.${String(minor % 100).padStart(2, '0')}`;
}

/**
 * Quotes a CSV cell when it needs it — and, first, neutralises text a spreadsheet would run as a formula (a stock item
 * or a product someone named "=HYPERLINK(…)"), as the server's export does (`apps/server/src/http/csv.ts`): a leading
 * `=`, `+`, `-`, `@`, tab or carriage return gets a single quote in front. A plain signed figure ("-12.50") stays a
 * number.
 */
function csvCell(value: string | number): string {
  let text = String(value);
  if (typeof value === 'string' && /^\s*[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The CSV text of a header and rows (RFC 4180, CRLF). */
export function csvOf(header: readonly string[], rows: readonly (readonly (string | number)[])[]): string {
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

/** "Export CSV": the tab's table as a file the browser saves. */
export function ExportCsv({ fileName, header, rows }: { readonly fileName: string; readonly header: readonly string[]; readonly rows: readonly (readonly (string | number)[])[] }): ReactNode {
  const { t } = useTranslation('console');
  const download = () => {
    const blob = new Blob([`\ufeff${csvOf(header, rows)}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <Button variant="secondary" size="sm" onClick={download} disabled={rows.length === 0}>
      {t('reports.exportCsv')}
    </Button>
  );
}
